/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { cancel, call, fork, put, select, take } from "typed-redux-saga";
import { getType } from "typesafe-actions";
import type { Task } from "redux-saga";
import { wrap } from "@/modules/comlink";
import { rootActions } from "@/store";
import { getGlobalConfigs } from "@/index";
import {
  selectFilteredJobs,
  selectFocusedJobId,
  selectFocusedMachineJobs,
  selectStartAndEnd,
} from "@/store/selectors";
import {
  fetchMeasurementsForSelectedJob,
  getRecentSameNameJobRanges,
} from "@/store/sagas/shared";
import type { JobPresentation } from "@/presentation/Job";
import type { Exposed } from "./jobStatistics.worker";

/**
 * Single owner of the job-statistics calculation lifecycle.
 *
 * We only need to react to the selection/filter signals that can change the result,
 * cancel any stale in-flight task, and start a fresh calculation for the current
 * focused job.
 */
export function* jobStatisticsWatcher() {
  let task: Task | undefined;
  let lastKey: string | undefined;

  const selectedJobIdChangeCandidateTypes = [
    getType(rootActions.session.focusedJobIdUpdated),
    getType(rootActions.machineAnalysis.jobTimelineClicked),
    getType(rootActions.machineAnalysis.mostRecentJobUpdated),
    getType(rootActions.machineAnalysis.jobSummariesFetched),
    getType(rootActions.machineAnalysis.jobMeasurementsFetched),
  ];
  const filterType = getType(rootActions.machineAnalysis.jobFilterApplied);
  const jobMeasurementsFetchedType = getType(
    rootActions.machineAnalysis.jobMeasurementsFetched
  );

  while (true) {
    const action = (yield* take([
      ...selectedJobIdChangeCandidateTypes,
      filterType,
    ])) as { type: string; payload?: { jobName?: string } };

    const selectedJob = yield* selectFocusedJobForStatistics();
    if (!selectedJob) continue;

    const nextKey = `${selectedJob.id}:${selectedJob.measurementCharacteristics?.length ?? 0}`;
    const fetchedMeasurementsForFocusedJob =
      action.type === jobMeasurementsFetchedType &&
      action.payload?.jobName === selectedJob.name;
    if (
      action.type !== filterType &&
      !fetchedMeasurementsForFocusedJob &&
      nextKey === lastKey
    )
      continue;

    lastKey = nextKey;

    if (task?.isRunning()) {
      yield* cancel(task);
    }

    yield* put(rootActions.machineAnalysis.jobStatisticsCalculationStarted());
    task = yield* fork(calculateSelectedJobStatistics, selectedJob);
  }
}

function* selectFocusedJobForStatistics() {
  const focusedJobId = yield* select(selectFocusedJobId);
  if (!focusedJobId) return;

  const jobs = yield* select(selectFocusedMachineJobs);
  const focusedJob = jobs.find(({ id }) => id === focusedJobId);

  if (!focusedJob?.name || !focusedJob.machineId) return;
  if (focusedJob.type !== "metrology" && focusedJob.type !== "mastering") {
    return;
  }

  return focusedJob;
}

function* calculateSelectedJobStatistics(job: JobPresentation) {
  const worker = new Worker("./jobStatisticsWorker.js");
  try {
    const [from, to] = yield* select(selectStartAndEnd);
    const filteredJobs = yield* select(selectFilteredJobs);
    const config = yield* call(() => getGlobalConfigs());
    const ranges = getRecentSameNameJobRanges(
      filteredJobs,
      job.name,
      to,
      config.maxJobsForStatistics
    );

    const measurements = yield* fetchMeasurementsForSelectedJob({
      from,
      to,
      machineId: job.machineId,
      ranges,
    });

    const proxy = wrap<Exposed>(worker);
    const statistics = yield* call(proxy.calculateJobStatistics, measurements);

    yield* put(
      rootActions.machineAnalysis.jobStatisticsFetched({
        statistics,
        totalJobRuns: ranges.length,
      })
    );
  } catch (error) {
    console.error("Error computing job statistics:", error);
  } finally {
    worker.terminate();
  }
}
